import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { User } from "@shared/models/auth";

async function fetchUser(): Promise<User | null> {
  const response = await fetch("/api/auth/user", {
    credentials: "include",
  });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }

  return response.json();
}

async function logout(): Promise<void> {
  await fetch("/api/logout", {
    method: "POST",
    credentials: "include",
  });
}

export function useAuth() {
  const queryClient = useQueryClient();
  const { data: user, isLoading } = useQuery<User | null>({
    queryKey: ["/api/auth/user"],
    queryFn: fetchUser,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  const logoutMutation = useMutation({
    mutationFn: logout,
    onSuccess: () => {
      // Clear all cached queries and user data
      queryClient.setQueryData(["/api/auth/user"], null);
      queryClient.clear();
      // Redirect to login page
      window.location.href = "/admin";
    },
  });

  // Default permissions per role (fallback when user has no explicit permissions)
  const DEFAULT_PERMS: Record<string, string[]> = {
    super_admin: ["inbox", "bookings", "companions", "rota", "drivers", "earnings", "ai_config", "site_settings", "users"],
    admin: ["inbox", "bookings", "companions", "rota", "drivers", "earnings", "ai_config", "site_settings"],
    hostess: ["inbox", "bookings", "companions", "rota", "drivers"],
    companion: ["inbox", "bookings", "rota", "earnings"],
    driver: ["inbox", "bookings", "drivers"],
  };

  const role = (user as any)?.role as string | undefined;
  const rawPerms = (user as any)?.permissions as string[] | null;
  const permissions = (rawPerms && rawPerms.length > 0) ? rawPerms : (role ? DEFAULT_PERMS[role] || [] : []);

  const hasPermission = (key: string) => {
    if (role === "super_admin") return true;
    return permissions.includes(key);
  };

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    role: (user as any)?.role as string | undefined,
    permissions,
    hasPermission,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
  };
}
